import { Bell, CalendarDays, Circle, CircleCheckBig, CircleDot, Clock3, X } from "lucide-react";
import { DEFAULT_AREA_COLOR, resolveTaskColors } from "@/lib/listColors";
import { formatDurationShort, formatTimeDisplay, parseDateKey } from "@/lib/time";
import {
  deriveTaskLifecycle,
  getExecutionControlLabel,
  getExecutionControlState,
  getTaskLifecycleLabel,
  type TaskLifecycle,
} from "@shared/taskLifecycle";

export type ComplexTaskItemTask = {
  id: number;
  title: string;
  details?: string | null;
  notes?: string | null;
  date?: string | null;
  status: "pending" | "done" | "skipped";
  startMinutes?: number | null;
  durationMin?: number | null;
  alertEnabled: boolean;
  startedAt?: Date | string | null;
  completedAt?: Date | string | null;
  listName?: string | null;
  barColor?: string | null;
  bgColor?: string | null;
  listColor?: string | null;
  listBgColor?: string | null;
};

export function TaskLifecycleIcon({ lifecycle }: { lifecycle: TaskLifecycle }) {
  if (lifecycle === "doing") return <CircleDot />;
  if (lifecycle === "done") return <CircleCheckBig />;
  if (lifecycle === "skipped") return <X />;
  return <Circle />;
}

function localMinutes(value: Date | string | null | undefined) {
  if (!value) return null;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return null;
  return date.getHours() * 60 + date.getMinutes();
}

function durationBetween(start: Date | string | null | undefined, end: Date | string | null | undefined) {
  if (!start || !end) return null;
  const startMs = new Date(start).getTime();
  const endMs = new Date(end).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return null;
  return Math.round((endMs - startMs) / 60_000);
}

function TimeDetails({
  start,
  end,
  duration,
}: {
  start: number | null;
  end: number | null;
  duration: number | null;
}) {
  if (start == null) return null;
  return (
    <>
      <span>{formatTimeDisplay(start)}</span>
      {end != null && <><span aria-hidden="true">→</span><span>{formatTimeDisplay(end)}</span></>}
      {duration != null && duration > 0 && <><Clock3 aria-hidden="true" /><span>{formatDurationShort(duration)}</span></>}
    </>
  );
}

export function ComplexTaskItem({
  task,
  active = false,
  selected = false,
  onClick,
  onStyleClick,
}: {
  task: ComplexTaskItemTask;
  active?: boolean;
  selected?: boolean;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onStyleClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) {
  const colors = resolveTaskColors({
    barColor: task.barColor,
    bgColor: task.bgColor,
    listColor: task.listColor ?? DEFAULT_AREA_COLOR,
    listBgColor: task.listBgColor ?? null,
  });
  const lifecycle = deriveTaskLifecycle(task);
  const executionState = getExecutionControlState(task);
  const isReady = lifecycle === "doing" && executionState === "start";
  const displayLifecycle: TaskLifecycle = isReady ? "pending" : lifecycle;
  const statusLabel = isReady
    ? getExecutionControlLabel("start")
    : getTaskLifecycleLabel(lifecycle);
  const scheduledStart = task.startMinutes ?? null;
  const scheduledDuration = task.durationMin && task.durationMin > 0 ? task.durationMin : null;
  const scheduledEnd = scheduledStart != null && scheduledDuration != null
    ? scheduledStart + scheduledDuration
    : null;
  const statusStart = localMinutes(task.startedAt);
  const statusEnd = localMinutes(task.completedAt);
  const statusDuration = durationBetween(task.startedAt, task.completedAt);
  const showStatusTiming = lifecycle === "doing" || lifecycle === "done";
  const displayStatusStart = showStatusTiming ? statusStart : null;
  const displayStatusEnd = showStatusTiming ? statusEnd : null;
  const displayStatusDuration = showStatusTiming ? statusDuration : null;
  const dateLabel = task.date
    ? parseDateKey(task.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : null;

  return (
    <div
      className={`complex-task-item${active ? " active" : ""}${selected ? " selected" : ""}`}
      style={{
        "--task-color": colors.bar,
        "--task-ink": colors.ink,
        "--task-active-background": colors.soft,
      } as React.CSSProperties}
    >
      <button
        type="button"
        className="ui-custom-style-button style-button"
        style={{ background: colors.soft, color: colors.ink }}
        aria-label="Edit display colors"
        title="Edit display colors"
        onClick={onStyleClick ?? onClick}
      >
        <span className="style-dot" style={{ background: colors.bar }} aria-hidden="true" />
      </button>
      <button type="button" className="card-button" onClick={onClick}>
        <span className="task-copy">
          <span className={`title${lifecycle === "skipped" ? " skipped" : ""}`}>{task.title}</span>
          {task.details && <span className="description">{task.details}</span>}
          {task.notes && <span className="notes">{task.notes}</span>}
          {(dateLabel || scheduledStart != null) && (
            <span className="detail-row schedule">
              <CalendarDays aria-hidden="true" />
              <strong className="detail-label">{dateLabel}</strong>
              <span className="detail-values"><TimeDetails start={scheduledStart} end={scheduledEnd} duration={scheduledDuration} /></span>
              {task.alertEnabled && <Bell aria-label="Alert on" />}
            </span>
          )}
          <span className="detail-row status">
            <TaskLifecycleIcon lifecycle={displayLifecycle} />
            <strong className="detail-label">{statusLabel}</strong>
            <span className="detail-values"><TimeDetails start={displayStatusStart} end={displayStatusEnd} duration={displayStatusDuration} /></span>
          </span>
        </span>
        {task.listName && (
          <span className="list-pill">
            <span className="dot" />
            <span>{task.listName}</span>
          </span>
        )}
      </button>
    </div>
  );
}
